import React, { useEffect, useState } from 'react';
import PFCHeader from './PFCHeaderComponent';
import PFCFooter from './PFCFooterComponent';
import PFCOtpComponent from './PFCOtpComponent';
import ConsentAdditionalMembersComponent from './ConsentAdditionalMembersComponent';
import { getBaseUrl } from '../../helpers/addUserWithotPayment';
import queryString from 'query-string';
import { normalizeConsentJourney } from './consentJourneyUtils';
import '../../assets/css/pfc.css';

const ShieldCheck = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M10 2L17 5.5V10C17 14 14 17.5 10 19C6 17.5 3 14 3 10V5.5L10 2Z" fill="#0066DC" opacity=".2" stroke="#0066DC" strokeWidth="1.5" />
    <path d="M7 10L9 12L13 8" stroke="#0066DC" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const CheckIcon = () => (
  <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
    <path d="M1.5 4.5L3.5 6.5L7.5 2.5" stroke="white" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const DECLARATION_READ_MORE_THRESHOLD = 120;

const Declaration = ({ id, text, accepted, onToggle }) => {
  const [expanded, setExpanded] = useState(false);
  const declarationText = String(text || '');
  const isLong = declarationText.trim().length > DECLARATION_READ_MORE_THRESHOLD;
  const checkboxId = `consent-declaration-${String(id).replace(/[^A-Za-z0-9_-]/g, '-')}`;

  return (
    <div className="pfc-decl-card">
      <input id={checkboxId} className="pfc-decl-checkbox" type="checkbox" checked={accepted} onChange={onToggle} />
      <span className={`pfc-decl-check ${accepted ? 'on' : ''}`} aria-hidden="true">
        {accepted && <CheckIcon />}
      </span>
      <div>
        <label className={`pfc-decl-text ${isLong && !expanded ? 'collapsed' : ''}`} htmlFor={checkboxId}>{declarationText}</label>
        {isLong && (
          <button className="pfc-decl-readmore" type="button" onClick={() => setExpanded(current => !current)}>
            {expanded ? 'Show Less' : 'Read More'}
          </button>
        )}
      </div>
    </div>
  );
};

const parseStoredValue = value => {
  if (!value) return null;
  if (typeof value !== 'string') return value;
  try {
    const parsed = JSON.parse(value);
    return typeof parsed === 'string' ? parseStoredValue(parsed) : parsed;
  } catch (error) {
    return null;
  }
};

const getFirstStep = journey => journey?.steps?.[0] || 'complete';

const getNextStep = (journey, currentStep) => {
  const currentIndex = journey?.steps?.indexOf(currentStep);
  if (currentIndex === undefined || currentIndex < 0) return 'complete';
  return journey.steps[currentIndex + 1] || 'complete';
};

const getPreviousStep = (journey, currentStep) => {
  const currentIndex = journey?.steps?.indexOf(currentStep);
  if (currentIndex === undefined || currentIndex <= 0) return null;
  return journey.steps[currentIndex - 1];
};

const PFCPreviewPageComponent = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [review, setReview] = useState(null);
  const [consentJourney, setConsentJourney] = useState(null);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [currentScreen, setCurrentScreen] = useState(null);
  const [linkToken, setLinkToken] = useState('');
  const [otpDataId, setOtpDataId] = useState(null);
  const [paymentError, setPaymentError] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('loading');
  const [paymentStatusMessage, setPaymentStatusMessage] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [acceptedDeclarations, setAcceptedDeclarations] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    const parsed = queryString.parse(window.location.search);
    const token = parsed.token;
    setLinkToken(token || '');
    if (!token) {
      setError('Invalid link. Token not found.');
      setLoading(false);
      return;
    }
    resolveToken(token);
  }, []);

  const applyResolvedData = responseData => {
    const journey = normalizeConsentJourney(
      responseData.consentJourney,
      responseData.review?.package?.totalMembers,
    );
    setReview(responseData.review);
    setConsentJourney(journey);
    setAcceptedDeclarations({});
    return journey;
  };

  const startStep = async (step, responseData) => {
    if (step === 'otp') {
      await handleSendOtp(responseData?.review);
    } else if (step === 'payment') {
      await handleCreatePaymentLink(responseData?.review);
    } else {
      setCurrentScreen(step);
    }
  };

  const resolveToken = async token => {
    try {
      const res = await fetch(`${getBaseUrl()}/patientswebapp/affinity/resolveToken`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (data.success !== 1 || !data.data) {
        const errMsg = (data.error || '').toLowerCase();
        if (errMsg.includes('expired') || res.status === 401) {
          setSessionExpired(true);
        } else {
          setError(data.error || 'Invalid or expired link. Please request a new link.');
        }
        setLoading(false);
        return;
      }

      const journey = applyResolvedData(data.data);
      if (['completed', 'success'].includes(String(data.data.userStatus || '').toLowerCase())) {
        setPaymentCompleted(true);
        return;
      }

      await startStep(getFirstStep(journey), data.data);
    } catch (e) {
      setSessionExpired(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async (reviewData = review) => {
    if (!reviewData || !linkToken) {
      setError('Invalid link. Token not found.');
      return false;
    }

    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${getBaseUrl()}/patientswebapp/affinity/sendAffinityCustomerOTP`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Affinity-Token': linkToken,
        },
        body: JSON.stringify({
          phonenumber: reviewData.customer.phone,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.isSuccess !== true || !data.otpDataId) {
        setError(data.error || 'Unable to send OTP. Please try again.');
        return false;
      }

      setOtpDataId(data.otpDataId);
      setCurrentScreen('otp');
      return true;
    } catch (e) {
      setError('Unable to send OTP. Please try again.');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentWindowClosed = () => {
    setPaymentStatus('pending');
    setPaymentStatusMessage('Your activation is being processed. You will receive a confirmation SMS/email shortly.');
    setCurrentScreen('status');
  };

  const handleCreatePaymentLink = async (reviewData = review) => {
    if (!reviewData || !linkToken) return;
    setSubmitting(true);
    setPaymentError('');
    setPaymentStatus('loading');
    setPaymentStatusMessage('');
    setTransactionId('');
    setCurrentScreen('payment');
    try {
      const res = await fetch(`https://affiliate.medibuddy.in/upsell/iho/createPaymentLink`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          packageId: reviewData.package.id,
          customerName: reviewData.customer.name,
          phoneNumber: reviewData.customer.phone,
          email: reviewData.customer.email,
          audId: reviewData.customer.id,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setPaymentError(data.error || data.message || 'Failed to create payment link.');
        return;
      }
      if (!data.paymentLink && data.status === 'pending') {
        setPaymentCompleted(true);
        setSubmitting(false);
        return;
      }
      if (!data.paymentLink) {
        setPaymentError('Unable to create payment link. Please try again.');
        return;
      }
      const w = 500, h = 600;
      const left = window.screenX + (window.outerWidth - w) / 2;
      const top = window.screenY + (window.outerHeight - h) / 2;
      const payWin = window.open(data.paymentLink, 'RazorpayPayment', `width=${w},height=${h},left=${left},top=${top}`);
      if (!payWin) {
        setPaymentError('Popup blocked. Please allow popups and try again.');
        return;
      }
      const timer = setInterval(() => {
        if (payWin.closed) {
          clearInterval(timer);
          setTimeout(() => {
            handlePaymentWindowClosed();
          }, 2000);
        }
      }, 500);
    } catch (e) {
      setPaymentError('Error creating payment link. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReviewContinue = async () => {
    const requiredDeclarations = consentJourney.declarations.filter(declaration => declaration.required);
    const declarationsAccepted = !consentJourney.declarationsRequired || requiredDeclarations.every(declaration => acceptedDeclarations[declaration.id]);
    if (!declarationsAccepted) return;

    await startStep(getNextStep(consentJourney, 'review'));
  };

  const handleOtpVerified = async () => {
    await startStep(getNextStep(consentJourney, 'otp'));
  };

  const handleMembersNext = async responseData => {
    const journey = applyResolvedData(responseData);
    await startStep(getNextStep(journey, 'additionalMembers'));
  };

  const handleDeclarationToggle = id => {
    setAcceptedDeclarations(current => ({ ...current, [id]: !current[id] }));
  };

  if (loading) {
    return (
      <div className="pfc-portal">
        <PFCHeader />
        <div className="pfc-consent-loading">
          <div className="pfc-spinner" />
          <div className="pfc-consent-loading-text">Loading your details...</div>
        </div>
      </div>
    );
  }

  if (sessionExpired) {
    return (
      <div className="pfc-portal">
        <PFCHeader />
        <div className="pfc-consent-error-wrap">
          <div className="pfc-consent-error-icon">⏱</div>
          <div className="pfc-consent-error-title">Session Expired</div>
          <div className="pfc-consent-error-msg">Your link has expired. Please request a new link to continue.</div>
        </div>
        <PFCFooter />
      </div>
    );
  }

  if (error && !review) {
    return (
      <div className="pfc-portal">
        <PFCHeader />
        <div className="pfc-consent-error-wrap">
          <div className="pfc-consent-error-icon">!</div>
          <div className="pfc-consent-error-title">Something went wrong</div>
          <div className="pfc-consent-error-msg">{error}</div>
        </div>
        <PFCFooter />
      </div>
    );
  }

  if (paymentCompleted) {
    return (
      <div className="pfc-portal">
        <PFCHeader />
        <div className="pfc-consent-container">
          <div className="pfc-consent-success">
            <div className="pfc-success-icon-wrap">
              <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                <path d="M8 22L16 30L36 12" stroke="#26935F" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="pfc-success-title">Membership Activated!</div>
            <div className="pfc-success-desc">Welcome to MediBuddy. Your membership is active.</div>
          </div>
          <div className="pfc-consent-card">
            <div className="pfc-consent-card-label">Customer Details</div>
            <div className="pfc-customer-row">
              <div className="pfc-customer-avatar">{(review.customer.name || '?').charAt(0).toUpperCase()}</div>
              <div>
                <div className="pfc-customer-name">{review.customer.name}</div>
                <div className="pfc-customer-phone">+91 {review.customer.phone}</div>
              </div>
            </div>
          </div>
        </div>
        <PFCFooter />
      </div>
    );
  }

  if (currentScreen === 'additionalMembers' && review) {
    return (
      <ConsentAdditionalMembersComponent
        token={linkToken}
        initialMembers={review.customer.additionalMembers}
        maxMembers={consentJourney.maxAdditionalMembers}
        required={consentJourney.additionalMembersRequired}
        onNext={handleMembersNext}
        onBack={getPreviousStep(consentJourney, 'additionalMembers') ? () => setCurrentScreen(getPreviousStep(consentJourney, 'additionalMembers')) : undefined}
      />
    );
  }

  if (currentScreen === 'otp' && review) {
    return (
      <PFCOtpComponent
        token={linkToken}
        customer={review.customer}
        otpDataId={otpDataId}
        onVerified={handleOtpVerified}
        onBack={getPreviousStep(consentJourney, 'otp') ? () => setCurrentScreen(getPreviousStep(consentJourney, 'otp')) : undefined}
      />
    );
  }

  if (currentScreen === 'payment' && review) {
    return (
      <div className="pfc-portal">
        <PFCHeader />
        <div className="pfc-payment-container">
          {paymentError ? (
            <>
              <div className="pfc-payment-error-icon">!</div>
              <div className="pfc-payment-title">We couldn&apos;t start payment</div>
              <div className="pfc-payment-message">{paymentError}</div>
              <button className="pfc-btn-primary" type="button" onClick={handleCreatePaymentLink} disabled={submitting}>
                {submitting ? 'Trying again...' : 'Try Again'}
              </button>
            </>
          ) : (
            <>
              <div className="pfc-payment-spinner" />
              <div className="pfc-payment-title">{submitting ? 'Preparing your payment' : 'Payment window opened'}</div>
              <div className="pfc-payment-message">
                {submitting ? 'Please wait while we prepare your secure payment link.' : 'Complete your payment in the secure window to activate your membership.'}
              </div>
            </>
          )}
        </div>
        <PFCFooter />
      </div>
    );
  }

  if (currentScreen === 'status' && review) {
    return (
      <div className="pfc-portal">
        <PFCHeader />
        <div className="pfc-payment-container">
          {paymentStatus === 'failed' ? (
            <>
              <div className="pfc-payment-error-icon">!</div>
              <div className="pfc-payment-title">Activation Not Completed</div>
              <div className="pfc-payment-message">{paymentStatusMessage || 'We were unable to activate your membership.'}</div>
              <button className="pfc-btn-primary" type="button" onClick={handleCreatePaymentLink} disabled={submitting}>
                {submitting ? 'Trying again...' : 'Try Again'}
              </button>
            </>
          ) : (
            <>
              <div className="pfc-payment-spinner" />
              <div className="pfc-payment-title">Activating your membership</div>
              <div className="pfc-payment-message">{paymentStatusMessage || 'Your activation is being processed. You will receive a confirmation SMS/email shortly.'}</div>
              <button className="pfc-btn-secondary" type="button" onClick={handleCreatePaymentLink} disabled={submitting}>
                {submitting ? 'Please wait...' : 'Pay Again'}
              </button>
            </>
          )}
        </div>
        <PFCFooter />
      </div>
    );
  }

  if (currentScreen === 'complete' && review) {
    return (
      <div className="pfc-portal">
        <PFCHeader />
        <div className="pfc-consent-container">
          <div className="pfc-consent-success">
            <div className="pfc-success-icon-wrap">
              <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                <path d="M8 22L16 30L36 12" stroke="#26935F" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="pfc-success-title">Consent Completed</div>
            <div className="pfc-success-desc">Your details and consent have been recorded successfully.</div>
          </div>
        </div>
        <PFCFooter />
      </div>
    );
  }

  const customer = review?.customer || {};
  const plan = review?.package || {};
  const requiredDeclarations = consentJourney.declarations.filter(declaration => declaration.required);
  const allDeclarationsAccepted = !consentJourney.declarationsRequired || requiredDeclarations.every(declaration => acceptedDeclarations[declaration.id]);
  const nextReviewStep = getNextStep(consentJourney, 'review');
  const reviewButtonText = nextReviewStep === 'otp' ? 'Continue to OTP' : nextReviewStep === 'payment' ? 'Continue to Payment' : 'Continue';
  const additionalMembers = Array.isArray(customer.additionalMembers) ? customer.additionalMembers : [];

  return (
    <div className="pfc-portal">
      <PFCHeader />

      <div className="pfc-consent-body pfc-consent-review-body">
        <div className="pfc-consent-left">
          <div className="pfc-consent-greeting">Hi {customer.name?.split(' ')[0] || 'Customer'} 👋</div>
          <div className="pfc-consent-subtitle">Please review your membership details and provide your consent to activate your plan.</div>

          <div className="pfc-consent-plan-card">
            <div className="pfc-consent-card-label">Plan Summary</div>
            <div className="pfc-consent-plan-row">
              <div className="pfc-consent-plan-icon"><ShieldCheck /></div>
              <div>
                <div className="pfc-consent-plan-name">{plan.packageName || 'Membership Plan'}</div>
                <div className="pfc-consent-plan-price">₹{Number(plan.price || 0).toLocaleString()}/year</div>
              </div>
              <span className="pfc-consent-plan-badge">SELECTED</span>
            </div>
            {plan.vendor && <div className="pfc-consent-plan-meta">Vendor: {plan.vendor}</div>}
            {plan.totalMembers && <div className="pfc-consent-plan-meta">Coverage: Up to {plan.totalMembers} member{plan.totalMembers > 1 ? 's' : ''}</div>}
          </div>

          <div className="pfc-consent-details-card">
            <div className="pfc-consent-details-header">
              <div className="pfc-consent-card-label">Customer Details</div>
            </div>
            <div className="pfc-consent-detail-row"><span>Full Name</span><span>{customer.name || 'N/A'}</span></div>
            <div className="pfc-consent-detail-row"><span>Date of Birth</span><span>{customer.dob ? new Date(customer.dob).toLocaleDateString() : 'N/A'}</span></div>
            <div className="pfc-consent-detail-row"><span>Mobile</span><span>{customer.phone ? `+91 ${customer.phone}` : 'N/A'}</span></div>
            <div className="pfc-consent-detail-row"><span>Email</span><span>{customer.email || 'N/A'}</span></div>
            <div className="pfc-consent-detail-row"><span>Gender</span><span>{customer.gender || 'N/A'}</span></div>
            {customer.loanAccountNumber && <div className="pfc-consent-detail-row"><span>LAN Number</span><span>{customer.loanAccountNumber}</span></div>}
            {customer.address && <div className="pfc-consent-detail-row"><span>Address</span><span>{customer.address}</span></div>}
            {customer.city && <div className="pfc-consent-detail-row"><span>City</span><span>{customer.city}</span></div>}
            {customer.state && <div className="pfc-consent-detail-row"><span>State</span><span>{customer.state}</span></div>}
            {customer.pincode && <div className="pfc-consent-detail-row"><span>Pincode</span><span>{customer.pincode}</span></div>}
          </div>

          {customer.nomineeDetails?.nominee && (
            <div className="pfc-consent-details-card">
              <div className="pfc-consent-details-header">
                <div className="pfc-consent-card-label">Nominee Details</div>
              </div>
              <div className="pfc-consent-detail-row"><span>Name</span><span>{customer.nomineeDetails.nominee.name || 'N/A'}</span></div>
              <div className="pfc-consent-detail-row"><span>Relationship</span><span>{customer.nomineeDetails.nominee.relationship || 'N/A'}</span></div>
              <div className="pfc-consent-detail-row"><span>Date of Birth</span><span>{customer.nomineeDetails.nominee.dob ? new Date(customer.nomineeDetails.nominee.dob).toLocaleDateString() : 'N/A'}</span></div>
            </div>
          )}

          {customer.nomineeDetails?.guardian && (
            <div className="pfc-consent-details-card">
              <div className="pfc-consent-details-header">
                <div className="pfc-consent-card-label">Guardian Details</div>
              </div>
              <div className="pfc-consent-detail-row"><span>Name</span><span>{customer.nomineeDetails.guardian.name || 'N/A'}</span></div>
              <div className="pfc-consent-detail-row"><span>Relationship</span><span>{customer.nomineeDetails.guardian.relationship || 'N/A'}</span></div>
              {customer.nomineeDetails.guardian.phone && <div className="pfc-consent-detail-row"><span>Mobile</span><span>+91 {customer.nomineeDetails.guardian.phone}</span></div>}
            </div>
          )}

          {additionalMembers.length > 0 && (
            <div className="pfc-consent-details-card pfc-additional-members-card">
              <div className="pfc-consent-details-header">
                <div>
                  <div className="pfc-consent-card-label">Additional Members</div>
                  <div className="pfc-consent-section-subtitle">Covered family members</div>
                </div>
                <span className="pfc-member-count-badge">{additionalMembers.length}</span>
              </div>
              <div className="pfc-consent-member-list">
                {additionalMembers.map((member, index) => (
                  <div className="pfc-consent-member-summary" key={`${member.name || 'member'}-${index}`}>
                    <div className="pfc-member-summary-avatar">{(member.name || '?').trim().charAt(0).toUpperCase()}</div>
                    <div className="pfc-member-summary-body">
                      <div className="pfc-member-summary-top">
                        <strong>{member.name || 'N/A'}</strong>
                        <span className="pfc-member-summary-relationship">{member.relationship || 'N/A'}</span>
                      </div>
                      <div className="pfc-member-summary-meta">
                        {member.dob && <span>DOB {new Date(member.dob).toLocaleDateString()}</span>}
                        {member.phone && <span>+91 {member.phone}</span>}
                      </div>
                      {member.email && <div className="pfc-member-summary-email">{member.email}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="pfc-consent-right">
          {consentJourney.declarationsRequired && (
            <>
              <div className="pfc-consent-right-title">Declarations</div>
              {consentJourney.declarations.map(declaration => (
                <Declaration
                  key={declaration.id}
                  id={declaration.id}
                  text={declaration.text}
                  accepted={Boolean(acceptedDeclarations[declaration.id])}
                  onToggle={() => handleDeclarationToggle(declaration.id)}
                />
              ))}
              <div className="pfc-consent-info-banner">Your consent is required before continuing.</div>
            </>
          )}
          {error && <div className="pfc-consent-error-banner">{error}</div>}
          <button
            className="pfc-btn-primary pfc-consent-continue-btn"
            onClick={handleReviewContinue}
            disabled={!allDeclarationsAccepted || submitting}
          >
            {submitting ? 'Please wait...' : reviewButtonText}
          </button>
        </div>
      </div>

      <PFCFooter />
    </div>
  );
};

export default PFCPreviewPageComponent;
